import { ReactNode, useEffect } from "react";

import { cn } from "@/lib/utils";

import { Button, ButtonProps } from "./button";

interface DialogFooterWithShortcutsProps {
  children: ReactNode;
  className?: string;
  showShortcuts?: boolean;
  onSubmit?: () => void;
}

interface ActionButtonProps extends ButtonProps {
  shortcut?: string | string[];
  children: ReactNode;
}

export const DialogFooterWithShortcuts = ({ 
  children, 
  className,
  showShortcuts = true,
  onSubmit
}: DialogFooterWithShortcutsProps) => {
  // Detect platform for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modKey = isMac ? '⌘' : 'Ctrl';

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        {children}
      </div>
      {showShortcuts && (
        <div className="flex items-center justify-end gap-4 text-xs text-muted-foreground">
          {onSubmit && (
            <div className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">
                {modKey}
              </kbd>
              <span>+</span>
              <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">
                Enter
              </kbd>
              <span>or</span>
              <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">
                Enter
              </kbd>
              <span>to submit</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">
              Esc
            </kbd>
            <span>to cancel</span>
          </div>
        </div>
      )}
    </div>
  );
};

export const CancelButton = ({ children, ...props }: ActionButtonProps) => {
  return (
    <Button variant="outline" {...props}>
      <span className="flex items-center gap-2">
        {children}
        <kbd className="hidden sm:inline px-1.5 py-0.5 text-[10px] font-semibold bg-muted/50 border border-border/50 rounded opacity-60">
          Esc
        </kbd>
      </span>
    </Button>
  );
};

export const SubmitButton = ({ children, ...props }: ActionButtonProps) => {
  return (
    <Button {...props}>
      <span className="flex items-center gap-2">
        {children}
        <kbd className="hidden sm:inline px-1.5 py-0.5 text-[10px] font-semibold bg-primary-foreground/20 border border-primary-foreground/30 rounded opacity-60">
          ↵
        </kbd>
      </span>
    </Button>
  );
};
